import { useState } from 'react';
import { api, type Me } from '../api';
import { OBJECTS } from '../config';
import { Home } from '../pages/Landing';
import { ResourceList } from './ResourceList';
import { CustomFieldsAdmin } from './CustomFieldsAdmin';
import { NotificationsBell } from './NotificationsBell';
import { GlobalSearch } from './GlobalSearch';
import { QuotesPage } from './QuotesPage';
import { PurchaseOrdersPage } from './PurchaseOrdersPage';
import { CallQueuePage } from './CallQueuePage';
import { CampaignsPage } from './CampaignsPage';
import { InboxPage } from './InboxPage';
import { DashboardPage } from './DashboardPage';
import { ReportsPage } from './ReportsPage';
import { SupportPage } from './SupportPage';
import { SchedulerPage } from './SchedulerPage';

export function AppShell({ me }: { me: Me }) {
  const [view, setView] = useState<string>('home');
  const canManageFields = me.permissions.includes('customfields.manage');
  const canSend = me.permissions.includes('campaign.send');
  const canAnalytics = me.permissions.includes('analytics.view');

  async function logout() {
    await api.post('/api/auth/logout').catch(() => undefined);
    window.location.reload();
  }

  // Single navigation list ordered by the CRM funnel flow: overview -> acquire ->
  // core records -> close (sales docs) -> activities -> analyze/operate -> admin.
  // Objects and feature pages interleave, so the order lives here (not in OBJECTS).
  const objectsByKey = new Map(OBJECTS.map((o) => [o.key, o]));
  const objectEnabled = (key: string) => {
    const o = objectsByKey.get(key);
    return !!o && (!o.module || me.modules.includes(o.module)); // e.g. Leads gated by the `leads` module
  };
  const NAV: { key: string; label?: string; object?: boolean; show?: boolean }[] = [
    { key: 'home', label: 'Home' },
    { key: 'dashboard', label: 'Dashboard', show: canAnalytics },
    { key: 'lead', object: true },
    { key: 'campaigns', label: 'Campaigns' },
    { key: 'call-queue', label: 'Call queue' },
    { key: 'inbox', label: 'Inbox' },
    { key: 'account', object: true },
    { key: 'contact', object: true },
    { key: 'deal', object: true },
    { key: 'quotes', label: 'Quotes' },
    { key: 'purchase-orders', label: 'Purchase orders' },
    { key: 'product', object: true },
    { key: 'solution', object: true },
    { key: 'task', object: true },
    { key: 'meeting', object: true },
    { key: 'reports', label: 'Reports', show: canAnalytics },
    { key: 'scheduler', label: 'Scheduler' },
    { key: 'support', label: 'Support' },
    { key: 'custom-fields', label: 'Custom fields', show: canManageFields },
  ];
  const activeObject = objectEnabled(view) ? objectsByKey.get(view) : undefined;

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">crmhub</div>
        <div className="muted" style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{me.tenant.name}</div>
        <nav className="stack">
          {NAV.map((item) => {
            if (item.show === false) return null;
            if (item.object) {
              if (!objectEnabled(item.key)) return null;
              const label = objectsByKey.get(item.key)!.label;
              return (
                <button key={item.key} className={view === item.key ? 'navitem active' : 'navitem'} onClick={() => setView(item.key)}>
                  {label}
                </button>
              );
            }
            return (
              <button key={item.key} className={view === item.key ? 'navitem active' : 'navitem'} onClick={() => setView(item.key)}>
                {item.label}
              </button>
            );
          })}
        </nav>
      </aside>

      <div className="main">
        <header className="topbar">
          <GlobalSearch />
          <div className="row" style={{ alignItems: 'center' }}>
            <NotificationsBell />
            <span className="muted">{me.user.email}</span>
            <button className="btn" onClick={() => void logout()}>Sign out</button>
          </div>
        </header>
        <div className="content">
          {view === 'home' && <Home me={me} />}
          {view === 'dashboard' && <DashboardPage />}
          {view === 'reports' && <ReportsPage />}
          {activeObject && <ResourceList object={activeObject} />}
          {view === 'call-queue' && <CallQueuePage />}
          {view === 'inbox' && <InboxPage />}
          {view === 'campaigns' && <CampaignsPage canSend={canSend} />}
          {view === 'support' && <SupportPage />}
          {view === 'scheduler' && <SchedulerPage />}
          {view === 'quotes' && <QuotesPage />}
          {view === 'purchase-orders' && <PurchaseOrdersPage />}
          {view === 'custom-fields' && <CustomFieldsAdmin />}
        </div>
      </div>
    </div>
  );
}
